import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Utensils, Pill, TrendingUp, Dumbbell, Apple,
  ChevronRight, Activity, Zap, Shield,
  Database, Cpu, Menu, X, Sun, Moon
} from 'lucide-react';
import '../styles/Home.css';
import translations, { detectBrowserLang } from './translations';

const solutionIcons = [Utensils, Pill, TrendingUp, Dumbbell];
const ecosystemIcons = [Apple, Pill, Dumbbell, Utensils];

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

const Home = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(detectBrowserLang());
  const [theme, setTheme] = useState(() => localStorage.getItem('metabolify-theme') || 'dark');
  const t = translations[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    localStorage.setItem('metabolify-theme', theme);
  }, [theme]);

  const scrollTo = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const currentYear = new Date().getFullYear();
  const pathColors = ['#22c55e', '#eab308', '#f97316', '#ef4444'];
  const navItems = [
    ['#about-section', t.nav.about],
    ['#solution-section', t.nav.solution],
    ['#how-section', t.nav.how],
    ['#ecosystem-section', t.nav.ecosystem],
  ];

  return (
    <div className="home-root" data-theme={theme}>
      {/* ── Navbar ── */}
      <nav className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <div className="nav__inner">
          <a href="/" className="nav__brand">
            <img src="/images/logo.png" alt="Metabolify" className="nav__logo" />
            <span className="nav__name">Metabolify</span>
          </a>

          <div className="nav__right">
            <div className="nav__links desktop-only">
              {navItems.map(([id, label]) => (
                <a key={id} onClick={e => scrollTo(e, id)}>{label}</a>
              ))}
            </div>

            <div className="nav__controls">
              <button className="theme-toggle" onClick={() => setTheme(prev => prev === 'dark' ? 'light' : 'dark')} aria-label="Toggle theme">
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>
              <div className="lang-toggle">
                <button className={lang === 'en' ? 'active' : ''} onClick={() => setLang('en')}>
                  <img src="/images/en.png" alt="EN" /><span className="lang-label">EN</span>
                </button>
                <button className={lang === 'tr' ? 'active' : ''} onClick={() => setLang('tr')}>
                  <img src="/images/tr.png" alt="TR" /><span className="lang-label">TR</span>
                </button>
              </div>
            </div>

            <button className="nav__hamburger" onClick={() => setMobileOpen(o => !o)} aria-label="Menu">
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              className="nav__mobile"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              {navItems.map(([id, label]) => (
                <a key={id} onClick={e => scrollTo(e, id)}>{label}</a>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ── Hero ── */}
      <section className="hero">
        <div className="hero__orbs">
          <div className="orb orb--1" />
          <div className="orb orb--2" />
          <div className="orb orb--3" />
        </div>
        <div className="hero__grid-overlay" />

        <div className="hero__inner">
          <div className="hero__text">
            <motion.div
              className="badge"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Zap size={14} />
              <span>{t.cta.coming}</span>
            </motion.div>

            <motion.h1
              className="hero__title"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Metabolify
            </motion.h1>

            <motion.p
              className="hero__subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              className="hero__actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <a onClick={e => scrollTo(e, '#about-section')} className="btn btn--primary">
                {t.hero.discover}
                <ChevronRight size={18} />
              </a>
              <a onClick={e => scrollTo(e, '#contact-section')} className="btn btn--ghost">
                {t.hero.getStarted}
              </a>
            </motion.div>
          </div>

          <motion.div
            className="hero__visual"
            initial={{ opacity: 0, x: 80, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="dash-preview">
              <div className="dash-preview__header">
                <div className="dash-preview__dots">
                  <span /><span /><span />
                </div>
                <span className="dash-preview__title">Metabolify Dashboard</span>
              </div>
              <div className="dash-preview__body">
                <div className="dash-preview__sidebar">
                  {[Activity, Database, Shield, Cpu].map((Icon, i) => (
                    <div key={i} className={`sidebar-icon ${i === 0 ? 'active' : ''}`}>
                      <Icon size={18} />
                    </div>
                  ))}
                </div>
                <div className="dash-preview__main">
                  <div className="mini-cards">
                    <div className="mini-card">
                      <span className="mini-card__label">Glucose</span>
                      <span className="mini-card__value">92 <small>mg/dL</small></span>
                    </div>
                    <div className="mini-card mini-card--accent">
                      <span className="mini-card__label">HbA1c</span>
                      <span className="mini-card__value">5.4 <small>%</small></span>
                    </div>
                  </div>
                  <div className="mini-chart">
                    <svg viewBox="0 0 200 60" className="chart-svg">
                      <defs>
                        <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00d4aa" stopOpacity="0.3" />
                          <stop offset="100%" stopColor="#00d4aa" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M0,45 Q25,35 50,38 T100,28 T150,32 T200,20"
                        fill="none" stroke="#00d4aa" strokeWidth="2"
                        className="chart-line-anim"
                      />
                      <path
                        d="M0,45 Q25,35 50,38 T100,28 T150,32 T200,20 L200,60 L0,60 Z"
                        fill="url(#chartGrad)"
                        className="chart-area-anim"
                      />
                    </svg>
                  </div>
                  <div className="mini-bars">
                    {[65, 45, 80, 55, 70, 40, 60].map((h, i) => (
                      <div
                        key={i}
                        className="mini-bar"
                        style={{ '--bar-h': `${h}%`, '--bar-delay': `${i * 0.12}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── About / Problem ── */}
      <section id="about-section" className="section section--dark">
        <div className="section__inner">
          <Reveal>
            <h2 className="section__title">{t.about.title}</h2>
          </Reveal>

          <div className="about__grid">
            <Reveal className="about__text">
              <h3>{t.about.heading}</h3>
              <p>{t.about.text1}</p>
              <ul className="about__list">
                {t.about.points.map((pt, i) => (
                  <li key={i}>
                    <span className="about__bullet" />
                    {pt}
                  </li>
                ))}
              </ul>
              <p>{t.about.text}</p>
            </Reveal>

            <Reveal delay={0.2} className="about__visual">
              <div className="pathway">
                {t.about.map.map((label, i) => (
                  <React.Fragment key={i}>
                    <div className="pathway__node" style={{ '--node-color': pathColors[i] }}>
                      <div className="pathway__dot" />
                      <span>{label}</span>
                    </div>
                    {i < 3 && (
                      <div className="pathway__connector">
                        <div className="pathway__pulse" style={{ '--node-color': pathColors[i] }} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Solution ── */}
      <section id="solution-section" className="section section--surface">
        <div className="section__inner">
          <Reveal>
            <h2 className="section__title">{t.solution.title}</h2>
          </Reveal>

          <div className="solution__layout">
            <Reveal className="solution__info">
              <h3>{t.solution.heading}</h3>
              <p>{t.solution.text}</p>
            </Reveal>

            <div className="solution__cards">
              {t.solution.features.map((f, i) => {
                const Icon = solutionIcons[i];
                return (
                  <Reveal key={i} delay={i * 0.1}>
                    <div className="glass-card">
                      <div className="glass-card__icon">
                        <Icon size={28} />
                      </div>
                      <h4>{f.title}</h4>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-section" className="section section--dark">
        <div className="section__inner">
          <Reveal>
            <h2 className="section__title">{t.how.title}</h2>
          </Reveal>

          <div className="steps">
            {t.how.steps.map(([title, desc], i) => (
              <Reveal key={i} delay={i * 0.12}>
                <div className="step">
                  <div className="step__number">{String(i + 1).padStart(2, '0')}</div>
                  <div className="step__content">
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Ecosystem ── */}
      <section id="ecosystem-section" className="section section--surface">
        <div className="section__inner">
          <Reveal>
            <h2 className="section__title">{t.ecosystem.title}</h2>
            <p className="section__subtitle">{t.ecosystem.intro}</p>
          </Reveal>

          <div className="eco__grid">
            {t.ecosystem.items.map(([, title, desc], i) => {
              const Icon = ecosystemIcons[i];
              return (
                <Reveal key={i} delay={i * 0.1}>
                  <div className="eco-card">
                    <div className="eco-card__glow" />
                    <div className="eco-card__icon">
                      <Icon size={32} />
                    </div>
                    <h4>{title}</h4>
                    <p>{desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section id="contact-section" className="section cta">
        <div className="cta__orbs">
          <div className="orb orb--4" />
          <div className="orb orb--5" />
        </div>
        <div className="section__inner cta__inner">
          <Reveal>
            <div className="badge badge--lg">
              <Zap size={14} />
              <span>{t.cta.coming}</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="cta__title">{t.cta.title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="cta__text">{t.cta.text}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <a href="#" className="btn btn--primary btn--lg">
              {t.cta.learn}
              <ChevronRight size={18} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="site-footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <img src="/images/logo.png" alt="Metabolify" />
            <span>Metabolify</span>
          </div>
          <p className="footer__copy">&copy; {currentYear} Metabolify. {t.rights.reserved}.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
